import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ListboxOption } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useCookies } from 'next-client-cookies';
import { useTranslations } from 'use-intl';
import Image from 'components/commercetools-ui/atoms/image';
import Listbox from 'components/commercetools-ui/atoms/listbox';
import { CheckCircleIcon } from 'components/icons';
import { DEFAULT_LOCALE } from 'helpers/constants/events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mapCountry } from 'helpers/utils/mapCountry';
import { desktop } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';
import { usePathname, useRouter } from 'i18n/routing';
import useAccount from 'frontastic/hooks/useAccount';
import { HeaderContentProps } from '../../types/gmsb-header';

export interface Props {
  contentConfig: HeaderContentProps;
}

const TranslationDropDown: FC<Props> = ({ contentConfig }) => {
  const { translationIconLight, ariaLabelTranslation } = contentConfig;
  const [currentLocale, setCurrentLocale] = useState<string>(DEFAULT_LOCALE);
  const router = useRouter();
  const currentPath = usePathname();
  const cookies = useCookies();
  const translate = useTranslations();
  const { loggedIn, updateUserPreferredLocale } = useAccount();
  const locale = (cookies.get('locale') as string) || 'en-US';
  const countries = mapCountry(locale.split('-')[1]);
  const searchParams = useSearchParams();
  useEffect(() => {
    const currentLocaleName = countries?.locales.find((current) => current.locale == locale)?.name ?? DEFAULT_LOCALE;
    setCurrentLocale(currentLocaleName);
  }, [countries, locale]);

  const changeLocale = async (newLocale: string) => {
    const params = new URLSearchParams(searchParams.toString());
    const currentLocaleFromUrl = params.get('hl');
    if (currentLocaleFromUrl === newLocale) return;
    if (loggedIn) {
      const res = await updateUserPreferredLocale(newLocale);
      if (!res.success) {
        console.error('Failed to update user preferred locale', newLocale);
      }
    }
    params.set('hl', newLocale);
    router.push(`${currentPath}?${params.toString()}`);
    router.refresh();
  };

  const [isDesktopSize] = useMediaQuery(desktop);
  const localesCount = (countries?.locales ?? []).length;
  const languageDropdownButtonElement = useMemo(() => {
    return (
      <div
        aria-hidden="true"
        className="dropdown__button flex w-full items-center justify-between py-8 lg:py-0"
      >
        <div className="flex items-center">
          <Image
            media={translationIconLight?.media}
            width={Number(translationIconLight?.media?.width || 20)}
            height={Number(translationIconLight?.media?.height || 20)}
            alt={ariaLabelTranslation}
            data-ffid="img-translation-icon"
          />
          <span className="pl-8 pr-4 text-14" data-ffid={`lbl-lang-${cleanString(currentLocale ?? '')}`}>
            {currentLocale}
          </span>
        </div>

        {localesCount > 1 && (
          <ChevronDownIcon data-ffid="img-translation-dropicon" strokeWidth={2} className="dropdown__arrow w-14" />
        )}
      </div>
    );
  }, [currentLocale, localesCount, translationIconLight, ariaLabelTranslation]);

  return (
    <div className="flex-0 dropdown relative flex flex-row items-center md:mt-0">
      {localesCount > 1 ? (
        <Listbox
          buttonElement={languageDropdownButtonElement}
          buttonAriaLabel={`${ariaLabelTranslation}: ${translate('common.selected')} ${currentLocale}`}
          dataFfid="btn-translation-drpdwn"
          optionsClassName={`bg-white dark:bg-gmsb-gray overflow-auto pl-8 pr-16 ${isDesktopSize ? 'absolute border rounded top-30 shadow-[0_2px_16px_rgba(0,0,0,0.12)] z-40' : 'relative'}`}
          value={locale}
          onChange={changeLocale}
        >
          {countries?.locales.map((region, index) => (
            <ListboxOption
              key={region.locale}
              value={region.locale}
              data-testid={`translation option - ${region?.name} `}
              data-ffid={`btn-translation-option-${region?.name.trim()?.replace(/[^a-zA-Z0-9]/g, '')}`}
              className="relative flex w-full items-center justify-start py-6 pl-32"
              aria-label={`${ariaLabelTranslation} ${region?.name} `}
            >
              {({ selected }) => (
                <>
                  {selected && <CheckCircleIcon className="absolute left-0 size-24 text-green-500" aria-hidden="true" />}
                  <span
                    className={`text-16 ${selected ? 'font-bold' : 'font-normal'}`}
                    data-ffid={`btn-translation-option-region-name-${index}`}
                  >
                    {region?.name}
                  </span>
                </>
              )}
            </ListboxOption>
          ))}
        </Listbox>
      ) : (
        languageDropdownButtonElement
      )}
    </div>
  );
};

export default TranslationDropDown;
